import { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API, apiClient } from "@/App";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import {
  Home,
  Clock,
  Wallet,
  User,
  MapPin,
  Navigation,
  Bike,
  Car,
  Star,
  Phone,
  Check,
  X,
  ChevronRight,
  LogOut,
  IndianRupee,
  TrendingUp,
  MapPinned,
  Locate,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Gift,
  MessageCircle,
  Settings,
  Bell,
  ArrowRight,
  Percent,
  Calendar,
  CreditCard,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { RideChat, RideCall, IncomingCallChecker } from "@/components/ChatCall";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";

const DriverDashboard = () => {
  const { user, logout, setUser } = useAuth();
  const { appName } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("home");
  const [isOnline, setIsOnline] = useState(user?.is_online || false);
  const locationWatchRef = useRef(null);

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "earnings") setActiveTab("earnings");
    else if (path === "history") setActiveTab("history");
    else if (path === "profile") setActiveTab("profile");
    else setActiveTab("home");
  }, [location]);

  const toggleOnline = async () => {
    try {
      const res = await axios.put(`${API}/drivers/${user.id}`, {
        is_online: !isOnline,
      });
      setIsOnline(!isOnline);
      setUser(res.data);
      toast.success(isOnline ? "You are now offline" : "You are now online");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const navItems = [
    { id: "home", icon: Home, label: "Home", path: "/driver" },
    { id: "earnings", icon: Wallet, label: "Earnings", path: "/driver/earnings" },
    { id: "history", icon: Clock, label: "History", path: "/driver/history" },
    { id: "profile", icon: User, label: "Profile", path: "/driver/profile" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-20">
      {/* Impersonation Banner */}
      <ImpersonationBanner />
      
      {/* Online Toggle Header */}
      <header className="bg-[#141414] p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold">{appName} Driver</h1>
          <p className="text-sm text-white/60">{isOnline ? "Accepting rides" : "Offline"}</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm ${isOnline ? "text-green-400" : "text-white/40"}`}>
            {isOnline ? "Online" : "Offline"}
          </span>
          <Switch
            checked={isOnline}
            onCheckedChange={toggleOnline}
            className="data-[state=checked]:bg-green-500"
            data-testid="online-toggle"
          />
        </div>
      </header>

      <Routes>
        <Route index element={<DriverHome isOnline={isOnline} />} />
        <Route path="earnings" element={<EarningsPage />} />
        <Route path="history" element={<DriverHistory />} />
        <Route path="profile" element={<DriverProfile />} />
      </Routes>

      {/* Bottom Navigation */}
      <nav className="mobile-nav bg-[#141414] border-white/10" data-testid="driver-nav">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => navigate(item.path)}
            className={`mobile-nav-item flex-1 ${
              activeTab === item.id ? "text-primary" : "text-white/40"
            }`}
            data-testid={`nav-${item.id}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs mt-1">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

const DriverHome = ({ isOnline }) => {
  const { user } = useAuth();
  const [pendingRides, setPendingRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showCall, setShowCall] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const locationWatchRef = useRef(null);

  // Handle incoming call
  const handleIncomingCall = useCallback((call) => {
    setIncomingCall(call);
    setShowCall(true);
  }, []);

  // Location sharing for active rides
  useEffect(() => {
    const startLocationSharing = () => {
      if (!activeRide?.id || !navigator.geolocation) return;

      setSharingLocation(true);
      
      // Send location immediately
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            await axios.post(
              `${API}/rides/${activeRide.id}/driver-location?driver_id=${user.id}`,
              { lat: position.coords.latitude, lng: position.coords.longitude }
            );
          } catch (err) {
            console.error("Failed to send initial location:", err);
          }
        },
        (err) => console.error("Geolocation error:", err),
        { enableHighAccuracy: true }
      );

      // Watch position and send updates
      locationWatchRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          try {
            await axios.post(
              `${API}/rides/${activeRide.id}/driver-location?driver_id=${user.id}`,
              { lat: position.coords.latitude, lng: position.coords.longitude }
            );
          } catch (err) {
            console.error("Failed to update location:", err);
          }
        },
        (err) => console.error("Watch position error:", err),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    };

    const stopLocationSharing = () => {
      if (locationWatchRef.current) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
        locationWatchRef.current = null;
      }
      setSharingLocation(false);
    };

    if (activeRide && ["accepted", "arrived", "in_progress"].includes(activeRide.status)) {
      startLocationSharing();
    } else {
      stopLocationSharing();
    }

    return () => stopLocationSharing();
  }, [activeRide?.id, activeRide?.status, user?.id]);

  // Fetch pending rides
  useEffect(() => {
    const fetchPendingRides = async () => {
      if (!isOnline || !user?.vehicle_type) return;
      try {
        const res = await axios.get(`${API}/rides/pending/${user.vehicle_type}`);
        setPendingRides(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchPendingRides();
    const interval = setInterval(fetchPendingRides, 5000);
    return () => clearInterval(interval);
  }, [isOnline, user]);

  // Check for active ride
  useEffect(() => {
    const checkActiveRide = async () => {
      try {
        const res = await axios.get(`${API}/rides/driver/${user.id}`);
        const active = res.data.find((r) =>
          ["accepted", "arrived", "in_progress"].includes(r.status)
        );
        if (active) setActiveRide(active);
        else setActiveRide(null);
      } catch (err) {
        console.error(err);
      }
    };
    if (user) checkActiveRide();
    const interval = setInterval(checkActiveRide, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const acceptRide = async (rideId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/rides/${rideId}/accept?driver_id=${user.id}`);
      setActiveRide(res.data);
      setPendingRides((prev) => prev.filter((r) => r.id !== rideId));
      toast.success("Ride accepted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept ride");
    } finally {
      setLoading(false);
    }
  };

  const updateRideStatus = async (status) => {
    try {
      const res = await axios.post(`${API}/rides/${activeRide.id}/status?status=${status}`);
      setActiveRide(res.data);
      if (status === "completed") {
        toast.success("Ride completed! ₹" + activeRide.fare + " earned");
        setActiveRide(null);
      } else {
        toast.success("Status updated");
      }
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const statusActions = {
    accepted: { label: "Arrived at Pickup", next: "arrived" },
    arrived: { label: "Start Trip", next: "in_progress" },
    in_progress: { label: "Complete Trip", next: "completed" },
  };

  if (!user?.vehicle_type) {
    return (
      <div className="p-4" data-testid="setup-vehicle-prompt">
        <div className="p-6 bg-amber-500/10 rounded-xl border border-amber-500/30 text-center">
          <Car className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-amber-400">Setup Required</h3>
          <p className="text-sm text-white/60 mt-2">
            Please complete your profile and add vehicle details to start accepting rides.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="driver-home-page">
      {/* Active Ride */}
      {activeRide && (
        <div className="mb-6 p-4 bg-[#1A1A1A] rounded-xl border border-white/10" data-testid="active-ride-card">
          <div className="flex items-center justify-between mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium status-${activeRide.status}`}>
              {activeRide.status.replace("_", " ").toUpperCase()}
            </span>
            <span className="text-2xl font-numbers font-bold text-green-400">
              ₹{activeRide.fare}
            </span>
          </div>

          {/* Location Sharing Indicator */}
          {sharingLocation && (
            <div className="flex items-center gap-2 p-2 mb-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
              <Locate className="w-4 h-4 text-blue-400 animate-pulse" />
              <span className="text-sm text-blue-400">Sharing location with customer</span>
            </div>
          )}

          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-semibold">{activeRide.customer_name}</p>
              <p className="text-sm text-white/60">{activeRide.distance_km} km • {activeRide.estimated_time_mins} mins</p>
            </div>
            {/* Chat Button */}
            <button
              onClick={() => setShowChat(true)}
              className="w-10 h-10 rounded-full bg-primary flex items-center justify-center"
              data-testid="chat-customer-btn"
            >
              <MessageCircle className="w-5 h-5 text-white" />
            </button>
            {/* Call Button */}
            <button
              onClick={() => { setIncomingCall(null); setShowCall(true); }}
              className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center"
              data-testid="call-customer-btn"
            >
              <Phone className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Incoming Call Checker */}
          <IncomingCallChecker 
            rideId={activeRide.id} 
            userRole="driver" 
            onIncomingCall={handleIncomingCall}
          />

          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
              <MapPin className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white/40 uppercase">Pickup</p>
                <p className="text-sm">{activeRide.pickup.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
              <Navigation className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white/40 uppercase">Drop</p>
                <p className="text-sm">{activeRide.drop.address}</p>
              </div>
            </div>
          </div>

          {statusActions[activeRide.status] && (
            <Button
              className="w-full h-14 rounded-full font-semibold text-lg bg-green-500 hover:bg-green-600"
              onClick={() => updateRideStatus(statusActions[activeRide.status].next)}
              data-testid="update-status-btn"
            >
              {statusActions[activeRide.status].label}
              <ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          )}
        </div>
      )}

      {/* Pending Rides */}
      {!activeRide && isOnline && (
        <div data-testid="pending-rides-section">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPinned className="w-5 h-5 text-primary" />
            Nearby Ride Requests
          </h2>

          {pendingRides.length === 0 ? (
            <div className="p-8 bg-[#1A1A1A] rounded-xl border border-white/10 text-center">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4 relative">
                <Bike className="w-8 h-8 text-white/40" />
                <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
              </div>
              <p className="text-white/60">Waiting for ride requests...</p>
              <p className="text-sm text-white/40 mt-1">Stay online to receive requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingRides.map((ride) => (
                <div
                  key={ride.id}
                  className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10"
                  data-testid={`pending-ride-${ride.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5 text-white/40" />
                      <span className="font-medium">{ride.customer_name}</span>
                    </div>
                    <span className="text-xl font-numbers font-bold text-green-400">
                      ₹{ride.fare}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                      <span className="text-white/70 line-clamp-1">{ride.pickup.address}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                      <span className="text-white/70 line-clamp-1">{ride.drop?.address || ride.dropoff?.address}</span>
                    </div>
                    {/* Tell Driver Mode Indicator */}
                    {ride.tell_driver_mode && (
                      <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-blue-500/20 rounded-lg border border-blue-500/30">
                        <MessageCircle className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-300 text-xs font-medium">
                          Customer will tell you the destination (~{ride.approximate_distance_km || ride.distance_km} km)
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 mb-4 text-sm text-white/50">
                    <span>{ride.distance_km} km{ride.tell_driver_mode ? ' (approx)' : ''}</span>
                    <span>•</span>
                    <span>{ride.estimated_time_mins || ride.duration_min} mins</span>
                  </div>

                  <Button
                    className="w-full h-12 rounded-full font-semibold bg-green-500 hover:bg-green-600"
                    onClick={() => acceptRide(ride.id)}
                    disabled={loading}
                    data-testid={`accept-ride-${ride.id}`}
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Accept Ride
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Offline Message */}
      {!isOnline && !activeRide && (
        <div className="p-8 bg-[#1A1A1A] rounded-xl border border-white/10 text-center" data-testid="offline-message">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <Bike className="w-8 h-8 text-white/40" />
          </div>
          <h3 className="text-lg font-semibold">You're Offline</h3>
          <p className="text-sm text-white/40 mt-2">
            Toggle online to start accepting ride requests
          </p>
        </div>
      )}

      {/* Chat Modal */}
      {showChat && activeRide && (
        <RideChat
          rideId={activeRide.id}
          userId={user.id}
          userRole="driver"
          otherPartyName={activeRide.customer_name}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Call Modal */}
      {showCall && activeRide && (
        <RideCall
          rideId={activeRide.id}
          userId={user.id}
          userRole="driver"
          otherPartyName={activeRide.customer_name}
          onClose={() => { setShowCall(false); setIncomingCall(null); }}
          isIncoming={!!incomingCall}
          incomingCallId={incomingCall?.id}
        />
      )}
    </div>
  );
};

const EarningsPage = () => {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissionOptions, setCommissionOptions] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [earningsRes, optionsRes, notifRes] = await Promise.all([
          axios.get(`${API}/drivers/${user.id}/earnings`),
          axios.get(`${API}/driver/${user.id}/available-commission-options`).catch(() => ({ data: { available_options: [], current_type: "percentage" } })),
          axios.get(`${API}/driver/${user.id}/notifications?unread_only=true`).catch(() => ({ data: [] }))
        ]);
        
        setEarnings(earningsRes.data);
        setCommissionOptions(optionsRes.data.available_options || []);
        setCurrentPlan(optionsRes.data.current_type);
        setNotifications(notifRes.data || []);
        
        // Check for pending commission change request
        const pendingReq = await axios.get(`${API}/driver/${user.id}/pending-commission-request`).catch(() => ({ data: null }));
        setPendingRequest(pendingReq.data);
      } catch (err) {
        toast.error("Failed to load earnings");
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchData();
  }, [user]);

  const handleRequestChange = async () => {
    if (!selectedPlan) {
      toast.error("Please select a plan");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        requested_type: selectedPlan.type,
        reason: changeReason || null,
      };
      
      // If prepaid plan, add payment details
      if (selectedPlan.is_prepaid && paymentRef) {
        payload.payment_reference = paymentRef;
        payload.payment_amount = parseFloat(paymentAmount) || selectedPlan.rate;
      }
      
      await axios.post(`${API}/driver/${user.id}/request-commission-change`, payload);
      toast.success("Commission change request submitted! Waiting for admin approval.");
      setShowChangePlanDialog(false);
      setSelectedPlan(null);
      setPaymentRef("");
      setPaymentAmount("");
      setChangeReason("");
      
      // Refresh pending request
      const pendingReq = await axios.get(`${API}/driver/${user.id}/pending-commission-request`).catch(() => ({ data: null }));
      setPendingRequest(pendingReq.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await axios.put(`${API}/driver/${user.id}/notifications/${notifId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (err) {
      console.error("Failed to mark notification as read");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const currentPlanInfo = commissionOptions.find(o => o.type === currentPlan);

  return (
    <div className="p-4" data-testid="earnings-page">
      {/* Notifications Banner */}
      {notifications.length > 0 && (
        <div className="mb-4 space-y-2">
          {notifications.slice(0, 3).map((notif) => (
            <div 
              key={notif.id}
              className={`p-3 rounded-lg border flex items-start gap-3 ${
                notif.type === 'commission_approved' ? 'bg-green-500/10 border-green-500/30' :
                notif.type === 'commission_rejected' ? 'bg-red-500/10 border-red-500/30' :
                'bg-blue-500/10 border-blue-500/30'
              }`}
            >
              <Bell className={`w-5 h-5 mt-0.5 ${
                notif.type === 'commission_approved' ? 'text-green-400' :
                notif.type === 'commission_rejected' ? 'text-red-400' :
                'text-blue-400'
              }`} />
              <div className="flex-1">
                <p className="text-sm font-medium">{notif.title}</p>
                <p className="text-xs text-white/60 mt-0.5">{notif.message}</p>
              </div>
              <button 
                onClick={() => markNotificationRead(notif.id)}
                className="text-white/40 hover:text-white/60"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Today's Earnings */}
      <div className="p-6 bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-2xl border border-green-500/30 mb-6">
        <p className="text-sm text-white/60 mb-1">Today's Earnings</p>
        <p className="text-4xl font-numbers font-bold text-green-400">
          ₹{earnings?.today_earnings || 0}
        </p>
        <p className="text-sm text-white/50 mt-2">
          {earnings?.today_rides || 0} rides completed today
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10">
          <IndianRupee className="w-6 h-6 text-primary mb-2" />
          <p className="text-2xl font-numbers font-bold">₹{earnings?.total_earnings || 0}</p>
          <p className="text-xs text-white/40 mt-1">Total Earnings</p>
        </div>
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10">
          <TrendingUp className="w-6 h-6 text-green-400 mb-2" />
          <p className="text-2xl font-numbers font-bold">{earnings?.total_rides || 0}</p>
          <p className="text-xs text-white/40 mt-1">Total Rides</p>
        </div>
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10">
          <Star className="w-6 h-6 text-amber-400 mb-2" />
          <p className="text-2xl font-numbers font-bold">{earnings?.avg_rating || 5.0}</p>
          <p className="text-xs text-white/40 mt-1">Rating</p>
        </div>
        <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10">
          <Clock className="w-6 h-6 text-blue-400 mb-2" />
          <p className="text-2xl font-numbers font-bold">{earnings?.today_rides || 0}</p>
          <p className="text-xs text-white/40 mt-1">Today's Rides</p>
        </div>
      </div>

      {/* Current Commission Plan */}
      <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10 mb-4" data-testid="commission-plan-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Commission Plan
          </h3>
          {!pendingRequest && commissionOptions.length > 1 && (
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setShowChangePlanDialog(true)}
              data-testid="change-plan-btn"
            >
              Change Plan
            </Button>
          )}
        </div>
        
        {/* Current Plan Display */}
        <div className="p-3 bg-primary/10 rounded-lg border border-primary/30 mb-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/60">Current Plan</p>
              <p className="font-semibold text-primary">
                {currentPlanInfo?.name || currentPlan?.replace(/_/g, ' ') || 'Percentage per Ride'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-primary">
                {currentPlanInfo?.is_prepaid ? `₹${currentPlanInfo?.rate}` : `${currentPlanInfo?.rate || 10}%`}
              </p>
              <p className="text-xs text-white/40">
                {currentPlanInfo?.is_prepaid ? 'prepaid' : 'per ride'}
              </p>
            </div>
          </div>
        </div>

        {/* Pending Request Notice */}
        {pendingRequest && (
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <div className="flex items-start gap-2">
              <Clock className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="font-medium text-amber-400">Change Request Pending</p>
                <p className="text-sm text-white/60 mt-1">
                  Requested: <span className="capitalize">{pendingRequest.requested_type?.replace(/_/g, ' ')}</span>
                </p>
                {pendingRequest.requires_payment && (
                  <p className="text-xs text-white/50 mt-1">
                    {pendingRequest.payment_verified 
                      ? '✓ Payment verified - waiting for final approval'
                      : '⏳ Payment verification pending'
                    }
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Change Plan Dialog */}
      <Dialog open={showChangePlanDialog} onOpenChange={setShowChangePlanDialog}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>Change Commission Plan</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-white/60">
              Select a new commission plan. Fixed plans require upfront payment.
            </p>
            
            {/* Plan Options */}
            <div className="space-y-2">
              {commissionOptions.filter(o => o.type !== currentPlan).map((option) => (
                <button
                  key={option.type}
                  onClick={() => setSelectedPlan(option)}
                  className={`w-full p-4 rounded-lg border text-left transition-all ${
                    selectedPlan?.type === option.type 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                  data-testid={`plan-option-${option.type}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{option.name}</p>
                      <p className="text-sm text-white/50">{option.description}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${option.is_prepaid ? 'text-blue-400' : 'text-green-400'}`}>
                        {option.is_prepaid ? `₹${option.rate}` : `${option.rate}%`}
                      </p>
                      {option.is_prepaid && (
                        <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                          Prepaid
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Payment Details for Prepaid Plans */}
            {selectedPlan?.is_prepaid && (
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30 space-y-3">
                <div className="flex items-center gap-2 text-blue-400">
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">Payment Required</span>
                </div>
                <p className="text-sm text-white/60">
                  Please pay <strong>₹{selectedPlan.rate}</strong> to your admin via UPI/Cash and enter the reference below.
                </p>
                <div>
                  <Label className="text-white/60">Payment Reference / Transaction ID</Label>
                  <Input
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder="e.g., UPI123456789"
                    className="mt-1 bg-white/5 border-white/10"
                    data-testid="payment-ref-input"
                  />
                </div>
                <div>
                  <Label className="text-white/60">Amount Paid (₹)</Label>
                  <Input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder={selectedPlan.rate.toString()}
                    className="mt-1 bg-white/5 border-white/10"
                  />
                </div>
              </div>
            )}

            {/* Reason (optional) */}
            <div>
              <Label className="text-white/60">Reason for change (optional)</Label>
              <Textarea
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="Why do you want to change your plan?"
                className="mt-1 bg-white/5 border-white/10 resize-none"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePlanDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestChange}
              disabled={!selectedPlan || submitting}
              data-testid="submit-change-request-btn"
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DriverHistory = () => {
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRides = async () => {
      try {
        const res = await axios.get(`${API}/rides/driver/${user.id}`);
        setRides(res.data);
      } catch (err) {
        toast.error("Failed to load trip history");
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchRides();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="driver-history-page">
      <h2 className="text-lg font-semibold mb-4">Trip History</h2>

      {rides.length === 0 ? (
        <div className="p-8 bg-[#1A1A1A] rounded-xl border border-white/10 text-center">
          <Clock className="w-12 h-12 text-white/20 mx-auto mb-3" />
          <p className="text-white/40">No trips yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rides.map((ride) => (
            <div
              key={ride.id}
              className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10"
              data-testid={`trip-${ride.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium status-${ride.status}`}>
                  {ride.status.replace("_", " ")}
                </span>
                <span className="text-lg font-numbers font-bold text-green-400">₹{ride.fare}</span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                  <span className="text-white/60 line-clamp-1">{ride.pickup.address}</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span className="text-white/60 line-clamp-1">{ride.drop.address}</span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                <span className="text-xs text-white/40">
                  {new Date(ride.created_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
                <div className="flex items-center gap-3 text-xs text-white/40">
                  <span>{ride.distance_km} km</span>
                  {ride.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                      <span>{ride.rating}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DriverProfile = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [vehicleType, setVehicleType] = useState(user?.vehicle_type || "");
  const [vehicleNumber, setVehicleNumber] = useState(user?.vehicle_number || "");
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    if (!vehicleType || !vehicleNumber) {
      toast.error("Please fill all fields");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/drivers/${user.id}`, {
        vehicle_type: vehicleType,
        vehicle_number: vehicleNumber,
      });
      setUser(res.data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  return (
    <div className="p-4" data-testid="driver-profile-page">
      {/* Profile Header */}
      <div className="p-6 bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl border border-primary/30 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{user?.name}</h2>
            <p className="text-sm text-white/60">{user?.email}</p>
            <div className="flex items-center gap-1 mt-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="text-sm">{user?.rating || 5.0} rating</span>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10 mb-4">
        <h3 className="text-sm font-medium text-white/60 mb-4">Vehicle Details</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-white/60">Vehicle Type</Label>
            <Select value={vehicleType} onValueChange={setVehicleType}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="vehicle-type-select">
                <SelectValue placeholder="Select vehicle type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bike">Bike</SelectItem>
                <SelectItem value="auto">Auto</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/60">Vehicle Number</Label>
            <Input
              placeholder="DL 01 AB 1234"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value)}
              className="mt-1 bg-white/5 border-white/10"
              data-testid="vehicle-number-input"
            />
          </div>
          <Button
            className="w-full"
            onClick={saveProfile}
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Contact Info */}
      <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10 mb-4">
        <h3 className="text-sm font-medium text-white/60 mb-3">Contact Info</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5 text-white/40" />
            <span>{user?.phone}</span>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <DriverDocuments userId={user?.id} />

      {/* Referral Code */}
      <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-5 h-5 text-primary" />
          <h3 className="text-sm font-medium text-white/60">Your Referral Code</h3>
        </div>
        <div className="p-3 bg-white/5 rounded-lg text-center">
          <span className="font-mono text-xl font-bold text-primary">{user?.referral_code || "LOADING..."}</span>
        </div>
        <p className="text-xs text-white/40 mt-2 text-center">Share this code with friends and earn rewards!</p>
      </div>

      <Button
        variant="outline"
        className="w-full h-12 border-red-500/50 text-red-400 hover:bg-red-500/10"
        onClick={handleLogout}
        data-testid="logout-btn"
      >
        <LogOut className="w-5 h-5 mr-2" />
        Logout
      </Button>
    </div>
  );
};

// Driver Documents Upload Component
const DriverDocuments = ({ userId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedType, setSelectedType] = useState(null);

  const documentTypes = [
    { key: "driving_license", label: "Driving License", required: true },
    { key: "vehicle_rc", label: "Vehicle RC", required: true },
    { key: "insurance", label: "Insurance", required: false },
    { key: "aadhaar", label: "Aadhaar Card", required: false },
  ];

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/drivers/${userId}/documents`);
      setDocuments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchDocuments();
  }, [userId]);

  const getDocStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    return doc?.status || null;
  };

  const handleFileSelect = (docType) => {
    setSelectedType(docType);
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedType) return;

    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Please upload an image or PDF");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(selectedType);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        await axios.post(`${API}/drivers/${userId}/documents`, {
          document_type: selectedType,
          document_data: event.target.result,
        });
        toast.success("Document uploaded! Pending verification.");
        fetchDocuments();
      } catch (err) {
        toast.error("Failed to upload document");
      } finally {
        setUploading(null);
        setSelectedType(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  if (loading) return null;

  return (
    <div className="p-4 bg-[#1A1A1A] rounded-xl border border-white/10 mb-4">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-white/60" />
        <h3 className="text-sm font-medium text-white/60">Documents</h3>
      </div>
      
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept="image/*,.pdf"
        className="hidden"
      />
      
      <div className="space-y-3">
        {documentTypes.map((docType) => {
          const status = getDocStatus(docType.key);
          const isUploading = uploading === docType.key;
          
          return (
            <div key={docType.key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center gap-3">
                {status === "verified" ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : status === "pending" ? (
                  <Clock className="w-5 h-5 text-yellow-500" />
                ) : status === "rejected" ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <FileText className="w-5 h-5 text-white/40" />
                )}
                <div>
                  <p className="text-sm font-medium">
                    {docType.label}
                    {docType.required && <span className="text-red-400 ml-1">*</span>}
                  </p>
                  <p className="text-xs text-white/40">
                    {status === "verified" && "Verified"}
                    {status === "pending" && "Pending review"}
                    {status === "rejected" && "Rejected - Please re-upload"}
                    {!status && "Not uploaded"}
                  </p>
                </div>
              </div>
              
              {status !== "verified" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleFileSelect(docType.key)}
                  disabled={isUploading}
                  className="text-xs"
                >
                  {isUploading ? "..." : <Upload className="w-4 h-4" />}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      
      <p className="text-xs text-white/40 mt-3">
        * Required documents for verification
      </p>
    </div>
  );
};

export default DriverDashboard;
